import { Dialog } from 'primereact/dialog'
import { Button } from 'primereact/button'
import { FC, useEffect, useState } from 'react'
import { Dropdown } from 'primereact/dropdown'
import { basicRunReferenceProjection } from '../../utils/constants'
import { DropdownStyle } from '../../utils/styling-constants'
import { SystemResult, WorkflowSubmission, WorkflowTask } from '@aero-platform/shared'
import {
  useAddWorkflowTask,
  useGetSubmissions,
  useUpdateReferenceRuns
} from '../../api/submission-API'
import { protoService } from '../../proto/ProtoService'
import IconButton from '../buttons/IconButton'
import { DeleteIcon, PlusIcon } from '../Icons'
import {
  ReferenceRunEntry,
  parseReferenceRunStrings,
  transformSubmissionsToReferenceRuns
} from '../../utils/helpers'
import toast from 'react-hot-toast'
import { useGetWorkflowTasks } from '../../api/workflow-task-API'

type ReferenceRunModalProps = {
  visible: boolean
  currentWorkflowSubmission: WorkflowSubmission
  selectedCaseName?: string
  referenceRuns: ReferenceRunEntry[]
  onHide: () => void
}

/**
 * Builds the query+projection string for fetching star-ccm+ completed runs.
 */
const buildStarCCMCompletedRunQuery = (nameFilter: string): string => {
  const filter = {
    name: encodeURIComponent(JSON.stringify({ $regex: nameFilter, $options: 'i' })),
    starccm: encodeURIComponent(JSON.stringify({ $exists: true }))
  }

  return `query=${encodeURIComponent(JSON.stringify(filter))}&proj=${basicRunReferenceProjection}`
}

/**
 * Builds the query string for fetching a single workflow task whose name contains
 * both "Ensight" and "Compare" (case-insensitive).
 */
const buildEnsightCompareWorkflowTaskQuery = (): string => {
  const filter = {
    name: encodeURIComponent(
      JSON.stringify({ $regex: '(?=.*Ensight)(?=.*Compare)', $options: 'i' })
    )
  }

  return `query=${encodeURIComponent(JSON.stringify(filter))}&limit=1`
}

const ReferenceRunsModal: FC<ReferenceRunModalProps> = ({
  visible,
  currentWorkflowSubmission,
  selectedCaseName: caseName,
  referenceRuns,
  onHide
}) => {
  const [runsToAdd, setRunsToAdd] = useState<ReferenceRunEntry[]>([])
  const [runToAdd, setRunToAdd] = useState<ReferenceRunEntry>()
  const [searchResults, setSearchResults] = useState<ReferenceRunEntry[]>()
  const [latestRunData, setLatestRunData] = useState<WorkflowSubmission[]>()
  const [existingReferenceRuns, setExistingReferenceRuns] = useState<ReferenceRunEntry[]>(
    referenceRuns ?? []
  )
  const [filterQuery, setFilterQuery] = useState<string>('')
  const [{ data: runData, loading: loadingRun }, fetchRuns] = useGetSubmissions()
  const [{ loading: submitting }, updateReferenceRuns] = useUpdateReferenceRuns()
  const [{ data: latestRunDataProto }, fetchLatestRuns] = useGetSubmissions()
  const [, addWorkflowTask] = useAddWorkflowTask()
  const [, fetchWorkflowTasks] = useGetWorkflowTasks()

  // Fetches the latest runs for the current workflow submission when the modal is opened or when the current workflow submission changes.
  useEffect(() => {
    if (runData && runData.protoBytesList.length) {
      const submissions = protoService.decodeProtoResponse(runData, 'workflowsubmission')
      setSearchResults(
        transformSubmissionsToReferenceRuns(
          submissions as WorkflowSubmission[],
          currentWorkflowSubmission,
          caseName
        )
      )
    } else {
      setSearchResults([])
    }
  }, [runData, currentWorkflowSubmission, caseName])

  useEffect(() => {
    currentWorkflowSubmission?._id &&
      fetchLatestRuns(`limit=10&proj=${basicRunReferenceProjection}`)
  }, [currentWorkflowSubmission?._id])

  useEffect(() => {
    if (latestRunDataProto && latestRunDataProto.protoBytesList.length) {
      setLatestRunData(protoService.decodeProtoResponse(latestRunDataProto, 'workflowsubmission'))
    }
  }, [latestRunDataProto])

  useEffect(() => {
    setExistingReferenceRuns(referenceRuns ?? [])
  }, [referenceRuns])

  const handleRemoveStagedRun = (run: ReferenceRunEntry) => {
    setRunsToAdd(prev => {
      const idx = prev.findIndex(r => r.name === run.name)
      if (idx === -1) return prev
      const next = [...prev]
      next.splice(idx, 1)
      return next
    })
  }

  const renderSectionHeader = (title: string, count: number, accent: 'existing' | 'staged') => (
    <div className="flex items-center gap-x-2 mb-2">
      <span className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
        {title}
      </span>
      <span
        className={`inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full text-xs font-semibold text-white ${
          accent === 'staged' ? 'bg-pending-run' : 'bg-status-closed'
        }`}>
        {count}
      </span>
    </div>
  )

  // Renders a reference run as a compact card with its cases shown as chips.
  const renderRunCard = (
    run: ReferenceRunEntry,
    variant: 'existing' | 'staged',
    formatCase: (c: string) => string
  ) => {
    const staged = variant === 'staged'
    return (
      <div
        key={run.name}
        className="rounded-md border border-gray-200 dark:border-neutral-70 overflow-hidden">
        <div className="flex items-center justify-between gap-x-2 px-3 py-1.5 bg-neutral-20 dark:bg-neutral-80">
          <div className="flex items-center gap-x-2 min-w-0">
            <span
              className={`h-2 w-2 shrink-0 rounded-full ${
                staged ? 'bg-pending-run' : 'bg-status-closed'
              }`}
            />
            <span className="font-medium text-sm truncate">{run.name}</span>
          </div>
          {staged && (
            <IconButton
              title="Remove Run"
              severity="danger"
              onClick={() => handleRemoveStagedRun(run)}
              icon={<DeleteIcon className="fill-white w-3.5 h-3.5" />}
            />
          )}
        </div>
        <div className="flex flex-wrap gap-1.5 px-3 py-2">
          {run.cases?.length ? (
            run.cases.map(c => (
              <span
                key={c}
                className="inline-flex items-center rounded px-2 py-0.5 text-xs font-medium bg-gray-100 dark:bg-neutral-70 text-gray-700 dark:text-gray-200">
                {formatCase(c)}
              </span>
            ))
          ) : (
            <span className="text-xs text-gray-400 italic">No cases</span>
          )}
        </div>
      </div>
    )
  }

  const handleAddRun = () => {
    if (!runToAdd) return
    setRunsToAdd(prev => {
      const existingIdx = prev.findIndex(r => r.name === runToAdd.name)
      // Can have a case where reference run was reprocessed with a new systemRunId and have the same name. In that case, we want to merge the cases into the existing entry rather than adding a new entry.
      if (existingIdx !== -1) {
        const next = [...prev]
        const existing = { ...next[existingIdx] }
        const existingCases = existing.cases ?? []
        const newCases = (runToAdd.cases ?? []).filter(c => !existingCases.includes(c))
        existing.cases = [...existingCases, ...newCases]
        next[existingIdx] = existing
        return next
      }
      return [...prev, { ...runToAdd }]
    })
    setRunToAdd(null)
  }

  const handleSubmit = async () => {
    if (!currentWorkflowSubmission?._id || !caseName || runsToAdd.length === 0) return

    const matchingWrg = currentWorkflowSubmission.submissionRunGroups
      ?.flatMap(srg => srg.workflowRunGroups ?? [])
      .find(wrg => wrg.systemRunId.includes(caseName))

    if (!matchingWrg) return

    const runId = currentWorkflowSubmission._id
    const workflowRunGroupSysId = matchingWrg.systemRunId
    const ensightWorkflowSystem = matchingWrg.workflow.find(w =>
      w.name.toLowerCase().includes('ensight')
    )
    const workflowSystemRunId = ensightWorkflowSystem?.systemRunId ?? ''
    const workflowTasksResult = await fetchWorkflowTasks(buildEnsightCompareWorkflowTaskQuery())
    const workflowTasks = protoService.decodeProtoResponse(
      workflowTasksResult.data,
      'workflowtask'
    ) as WorkflowTask[]

    const workflowTask = workflowTasks.length > 0 ? workflowTasks[0] : undefined

    if (!runId || !workflowRunGroupSysId || !workflowSystemRunId || !workflowTask) {
      toast.error('Missing required information to submit reference runs.')
      return
    }

    const compareJobAnalysis =
      workflowTask.rescaleJob?.jobanalyses.length > 0 &&
      workflowTask.rescaleJob?.jobanalyses.find(ja => ja.command.toLowerCase().includes('compare'))

    if (compareJobAnalysis?.command.trim() === '') {
      toast.error('Missing compare job analysis in the workflow task.')
      return
    }

    const command = compareJobAnalysis?.command ?? ''
    if (!command.trim()) {
      toast.error('Missing command in the compare job analysis.')
      return
    }

    const newRunsSubmitted = {}
    const failedRunsSubmitted = {}
    for (const run of runsToAdd) {
      // {runName: string, cases: string[]}
      for (const c of run.cases ?? []) {
        try {
          // The command should have --compare compare. We want to replace the compare with the actual case name
          const command = compareJobAnalysis?.command ?? ''
          const modifiedCommand = command.replace(
            '--compare compare',
            `--compare ${c.split('-')[1]}`
          )
          const modifiedWorkflowTask = {
            ...workflowTask,
            rescaleJob: {
              ...workflowTask.rescaleJob,
              jobanalyses: workflowTask.rescaleJob?.jobanalyses.map(ja => {
                if (ja.command.toLowerCase().includes('compare')) {
                  return {
                    ...ja,
                    command: modifiedCommand
                  }
                }
                return ja
              })
            }
          } as WorkflowTask
          console.log(modifiedWorkflowTask)
          // For each case in the run, submit to addWorkflowTask
          const submitted = await addWorkflowTask({
            runId,
            workflowRunGroupSysId,
            workflowSystemRunId,
            workflowTask: modifiedWorkflowTask
          } as any)

          if (submitted.status === SystemResult.SUCCESS) {
            if (!newRunsSubmitted[run.name]) {
              newRunsSubmitted[run.name] = []
            }
            newRunsSubmitted[run.name].push(c)
          } else {
            if (!failedRunsSubmitted[run.name]) {
              failedRunsSubmitted[run.name] = []
            }
            failedRunsSubmitted[run.name].push(c)
          }
        } catch (error) {
          if (!failedRunsSubmitted[run.name]) {
            failedRunsSubmitted[run.name] = []
          }
          failedRunsSubmitted[run.name].push(c)
        }
      }
    }

    const newReferenceRunStrings = Object.keys(newRunsSubmitted).flatMap(runName =>
      newRunsSubmitted[runName].map(c => `${runName}|${c}`)
    )

    if (newReferenceRunStrings.length > 0) {
      ;(updateReferenceRuns as any)({
        submissionId: currentWorkflowSubmission._id as string,
        systemRunId: matchingWrg.systemRunId,
        referenceRuns: newReferenceRunStrings
      })
    }
    // Merge the newly submitted runs (encoded as "<RunName>|<CaseName>" strings) into the
    // existing { name, cases } entries rather than pushing raw strings into the array.
    const newEntries = parseReferenceRunStrings(newReferenceRunStrings)
    setExistingReferenceRuns(prev => {
      const merged = [...prev]
      newEntries.forEach(entry => {
        const idx = merged.findIndex(r => r.name === entry.name)
        if (idx !== -1) {
          const existingCases = merged[idx].cases ?? []
          merged[idx] = {
            ...merged[idx],
            cases: [...existingCases, ...entry.cases.filter(c => !existingCases.includes(c))]
          }
        } else {
          merged.push(entry)
        }
      })
      return merged
    })

    if (Object.keys(failedRunsSubmitted).length > 0) {
      toast.error('Some reference runs failed to submit.')
    } else {
      toast.success('Reference runs updated successfully.')
    }
    setRunsToAdd(prevRuns => {
      return prevRuns.filter(run => !newRunsSubmitted[run.name])
    })
  }

  const footerContent = (
    <div className="flex justify-end items-center gap-x-2 w-[75%]">
      <Dropdown
        disabled={submitting}
        loading={loadingRun}
        value={runToAdd}
        pt={{
          ...DropdownStyle,
          root: {
            ...DropdownStyle.root,
            className: `flex-1 ${DropdownStyle.root.className}`
          }
        }}
        filter
        filterBy="name"
        dataKey="name"
        showClear
        onFilter={e => {
          if (e.filter.length < 2) {
            setFilterQuery('')
            return
          }
          setFilterQuery(e.filter)
          fetchRuns(buildStarCCMCompletedRunQuery(e.filter))
        }}
        onChange={e => {
          setRunToAdd(e.value ?? null)
        }}
        itemTemplate={(item: ReferenceRunEntry) => item?.name ?? null}
        options={
          filterQuery.length > 0
            ? searchResults
            : latestRunData
            ? transformSubmissionsToReferenceRuns(
                latestRunData,
                currentWorkflowSubmission,
                caseName
              )
            : searchResults
        }
        optionLabel="name"
        useOptionAsValue
        placeholder="Select Run"
        emptyMessage="&nbsp;&nbsp;Use Search to select run"
        emptyFilterMessage="&nbsp;&nbsp;No runs found"
      />
      <IconButton
        title="Add Reference Run"
        severity="secondary"
        disabled={!runToAdd || submitting}
        onClick={handleAddRun}
        icon={<PlusIcon className="fill-white w-4 h-4" />}
      />
      <Button
        label="Submit"
        severity="info"
        pt={{
          label: {
            className: `text-white`
          }
        }}
        disabled={runsToAdd.length === 0 || submitting}
        loading={submitting}
        onClick={handleSubmit}
      />
    </div>
  )

  return (
    <Dialog
      visible={visible}
      onHide={onHide}
      header={`Reference Runs for ${currentWorkflowSubmission?.name} ${
        caseName ? `(Case ${caseName.split('-')[1]})` : ''
      }`}
      modal
      className="w-[600px]"
      footer={footerContent}>
      <div className="flex flex-col gap-y-5 max-h-[55vh] py-2 overflow-auto">
        <section>
          {renderSectionHeader('Existing Reference Runs', existingReferenceRuns.length, 'existing')}
          {existingReferenceRuns.length > 0 ? (
            <div className="flex flex-col gap-y-2">
              {existingReferenceRuns.map(run => renderRunCard(run, 'existing', c => c))}
            </div>
          ) : (
            <p className="text-sm text-gray-400 italic">No reference runs saved yet.</p>
          )}
        </section>
        {runsToAdd.length > 0 && (
          <section>
            {renderSectionHeader('Runs to Add', runsToAdd.length, 'staged')}
            <div className="flex flex-col gap-y-2">
              {runsToAdd.map(run => renderRunCard(run, 'staged', c => c.split('-')[1] ?? c))}
            </div>
          </section>
        )}
      </div>
    </Dialog>
  )
}

export default ReferenceRunsModal
